import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, TrendingUp, Users, Award, Clock, FileText, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const Reports = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedSemester, setSelectedSemester] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [stats, setStats] = useState(null);
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchStats();
    fetchDistributions();
  }, [selectedClass, selectedSemester, selectedYear]);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch classes');
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedClass !== 'all') params.class_id = selectedClass;
      if (selectedSemester !== 'all') params.semester = selectedSemester;
      if (selectedYear !== 'all') params.year = parseInt(selectedYear);

      const response = await axios.get(`${API}/stats`, { params });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributions = async () => {
    try {
      const params = {};
      if (selectedClass !== 'all') params.class_id = selectedClass;
      if (selectedSemester !== 'all') params.semester = selectedSemester;
      if (selectedYear !== 'all') params.year = parseInt(selectedYear);

      const response = await axios.get(`${API}/distributions`, { params });
      setDistributions(response.data);
    } catch (error) {
      console.error('Failed to fetch distributions');
    }
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (selectedClass !== 'all') params.class_id = selectedClass;
      if (selectedSemester !== 'all') params.semester = selectedSemester;
      if (selectedYear !== 'all') params.year = parseInt(selectedYear);

      const response = await axios.get(`${API}/distributions/export`, { 
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = `voucher_report_${selectedClass}_${selectedSemester}_${selectedYear}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const exportChartsToExcel = () => {
    if (!stats) {
      toast.error('No data available to export');
      return;
    }

    // Prepare data for Excel
    const summaryData = [
      ['Metric', 'Value'],
      ['Total Distributions', stats.total_distributions || 0],
      ['Completed', stats.completed || 0],
      ['Pending', stats.pending || 0],
      ['Passed', stats.passed || 0],
      ['Failed', stats.failed || 0],
      ['Completion Rate', `${stats.completion_rate || 0}%`],
      ['Pass Rate', `${stats.pass_rate || 0}%`]
    ];

    const certTypeData = [
      ['Certification Type', 'Count'],
      ...(stats.by_certification_type || []).map(item => [item.name, item.value])
    ];

    const statusData = [
      ['Status', 'Count'],
      ...(stats.by_status || []).map(item => [item.name, item.value])
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Add sheets
    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

    if (certTypeData.length > 1) {
      const ws2 = XLSX.utils.aoa_to_sheet(certTypeData);
      XLSX.utils.book_append_sheet(wb, ws2, 'By Certification Type');
    }

    if (statusData.length > 1) {
      const ws3 = XLSX.utils.aoa_to_sheet(statusData);
      XLSX.utils.book_append_sheet(wb, ws3, 'By Status');
    }

    // Export
    const filename = `statistics_${selectedClass}_${selectedSemester}_${selectedYear}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);

    toast.success('Statistics exported to Excel');
  };

  const uniqueSemesters = [...new Set(classes.map(c => c.semester))];
  const uniqueYears = [...new Set(classes.map(c => c.year))].sort((a, b) => b - a);

  const getFilterDescription = () => {
    const parts = [];
    if (selectedClass !== 'all') {
      const cls = classes.find(c => c.id === selectedClass);
      if (cls) parts.push(`Class: ${cls.name}`);
    }
    if (selectedSemester !== 'all') parts.push(`Semester: ${selectedSemester}`);
    if (selectedYear !== 'all') parts.push(`Year: ${selectedYear}`);
    return parts.length > 0 ? parts.join(' • ') : 'All Classes';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Reports & Analytics
          </h2>
          <p className="text-slate-600 dark:text-slate-400">Comprehensive insights into voucher distributions and exam results</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={exportChartsToExcel}
            disabled={!stats || loading}
            variant="outline"
            data-testid="export-stats-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Statistics
          </Button>
          <Button
            onClick={exportToExcel}
            disabled={exporting || loading}
            data-testid="export-report-button"
            className="bg-gradient-to-r from-emerald-500 to-teal-400 text-white hover:from-emerald-600 hover:to-teal-500"
          >
            {exporting ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Exporting...</span>
              </div>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export Full Report
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle>Report Filters</CardTitle>
          <CardDescription>Filter reports by class, semester, and year</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="class-filter">Class</Label>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger id="class-filter" data-testid="class-filter" className="mt-2">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {classes.filter(c => !c.archived).map(cls => (
                    <SelectItem key={cls.id} value={cls.id}>
                      {cls.name} ({cls.semester} {cls.year})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="semester-filter">Semester</Label>
              <Select value={selectedSemester} onValueChange={setSelectedSemester}>
                <SelectTrigger id="semester-filter" data-testid="semester-filter" className="mt-2">
                  <SelectValue placeholder="Select semester" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Semesters</SelectItem>
                  {uniqueSemesters.map(sem => (
                    <SelectItem key={sem} value={sem}>{sem}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="year-filter">Year</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger id="year-filter" data-testid="year-filter" className="mt-2">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {uniqueYears.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {(selectedClass !== 'all' || selectedSemester !== 'all' || selectedYear !== 'all') && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                <span>Filtered by: <strong>{getFilterDescription()}</strong></span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      ) : stats ? (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Distributions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="text-3xl font-bold text-slate-800 dark:text-slate-100">
                    {stats.total_distributions || 0}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">Completed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                    <Award className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-slate-800 dark:text-slate-100">
                      {stats.completed || 0}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {stats.completion_rate || 0}% rate
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">Pass Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-slate-800 dark:text-slate-100">
                      {stats.pass_rate || 0}%
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {stats.passed || 0} passed
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">Pending</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="text-3xl font-bold text-slate-800 dark:text-slate-100">
                    {stats.pending || 0}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Distribution Pie Chart */}
            {stats.by_status && stats.by_status.length > 0 && (
              <Card className="border-slate-200 dark:border-slate-700">
                <CardHeader>
                  <CardTitle>Distribution by Status</CardTitle>
                  <CardDescription>Overview of voucher statuses</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stats.by_status}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {stats.by_status.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Certification Type Bar Chart */}
            {stats.by_certification_type && stats.by_certification_type.length > 0 && (
              <Card className="border-slate-200 dark:border-slate-700">
                <CardHeader>
                  <CardTitle>Distributions by Certification Type</CardTitle>
                  <CardDescription>Breakdown by certification category</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.by_certification_type}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" fill="#3b82f6" name="Count" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Pass/Fail Comparison */}
            {stats.completed > 0 && (
              <Card className="border-slate-200 dark:border-slate-700 lg:col-span-2">
                <CardHeader>
                  <CardTitle>Pass/Fail Distribution</CardTitle>
                  <CardDescription>Exam results breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[
                      { name: 'Passed', count: stats.passed, rate: stats.pass_rate },
                      { name: 'Failed', count: stats.failed, rate: (100 - stats.pass_rate).toFixed(1) }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-700 rounded shadow-lg">
                                <p className="font-semibold">{payload[0].payload.name}</p>
                                <p className="text-sm">Count: {payload[0].value}</p>
                                <p className="text-sm">Rate: {payload[0].payload.rate}%</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend />
                      <Bar dataKey="count" fill="#10b981" name="Students" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Detailed Statistics */}
          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle>Detailed Breakdown</CardTitle>
              <CardDescription>Complete statistical overview</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Total Vouchers Distributed</div>
                  <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{stats.total_distributions || 0}</div>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Completion Rate</div>
                  <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.completion_rate || 0}%</div>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Pass Rate</div>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.pass_rate || 0}%</div>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Exams Passed</div>
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.passed || 0}</div>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Exams Failed</div>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.failed || 0}</div>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Pending Submissions</div>
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.pending || 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="py-12">
            <div className="text-center text-slate-500">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No data available for the selected filters</p>
              <p className="text-sm mt-2">Try adjusting your filter criteria</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reports;
